import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AdminRepository from '../../services/AdminRepository.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminCompactListShimmer, AdminListPageShimmer } from '../common/Shimmer.jsx';
import { generateSecurePassword, validatePassword } from '../../utils/password.js';
import { MODAL_ANIMATION_DURATION } from '../../config/constants.js';
import { can } from '../../config/adminPermissions.js';
import { IconAlertTriangle, IconCheck, IconX, IconChevronDown } from '../common/AdminIcons.jsx';
import SortOrderToggle from '../common/SortOrderToggle.jsx';
import ListPagination from '../common/ListPagination.jsx';
import { PAGE_SIZE_OPTIONS } from '../../utils/listPagination.js';
import { formatVirtualEmail, getRoleLabel as getRoleLabelFromList, DEFAULT_ROLES } from '../../utils/roleStatsUtils.js';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import { useDestructiveAction } from '../../contexts/DestructiveActionContext.jsx';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminClinicList.css';

/**
 * ユーザー一覧コンポーネント
 */
const AdminUserList = ({
  users,
  clinics,
  clinicStats,
  onUpdate,
  initialClinicFilter,
  autoOpenCreateClinicId,
  autoOpenCreateSignal = 0,
  onAutoOpenComplete,
  onAutoOpenCreateComplete,
  onClearFilter,
  lockedClinicId = null,
  embedded = false,
  serverPagination = false,
  user = null,
}) => {
  const { showAlert } = useAdminDialog();
  const probe = useScreenProbe('users');
  const { requestDestructiveAction } = useDestructiveAction();
  const canDelete = can(user, 'users:delete');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClinicId, setFilterClinicId] = useState(() => lockedClinicId || 'all');
  const adminRepo = AdminRepository.getInstance();
  const [serverUsers, setServerUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [clinicDirectory, setClinicDirectory] = useState(() => adminRepo.clinicsCache || []);
  const debouncedSearch = useDebouncedValue(searchTerm, 400);
  const displayUsers = serverPagination ? serverUsers : users;

  const clinicOptions = useMemo(() => {
    if (clinics?.length) return clinics;
    return clinicDirectory;
  }, [clinics, clinicDirectory]);

  const getClinicUserCount = useCallback((clinicId) => {
    if (!clinicId) return 0;
    const fromStats = clinicStats[clinicId]?.userStats?.total;
    if (typeof fromStats === 'number') return fromStats;
    return displayUsers.filter((u) => u.clinicId === clinicId).length;
  }, [clinicStats, displayUsers]);

  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortKey, setSortKey] = useState('clinic');
  const [sortOrder, setSortOrder] = useState('asc');

  const filterSignature = `${debouncedSearch}|${filterClinicId}|${initialClinicFilter}|${lockedClinicId}|${filterRole}|${filterStatus}|${pageSize}|${sortKey}|${sortOrder}`;
  const lastFilterSignatureRef = useRef(filterSignature);

  const resolveListClinicId = useCallback(() => {
    if (lockedClinicId) return lockedClinicId;
    if (filterClinicId !== 'all') return filterClinicId;
    return initialClinicFilter || null;
  }, [lockedClinicId, filterClinicId, initialClinicFilter]);

  const buildListParams = useCallback((page = 1) => ({
    search: debouncedSearch,
    page,
    limit: pageSize,
    clinicId: resolveListClinicId(),
    role: filterRole !== 'all' ? filterRole : null,
    status: filterStatus !== 'all' ? filterStatus : null,
    sortKey,
    sortOrder,
  }), [debouncedSearch, pageSize, resolveListClinicId, filterRole, filterStatus, sortKey, sortOrder]);

  const applyPageData = useCallback((data, pageFallback = 1) => {
    setServerUsers(data.users || []);
    const total = Number(data.total);
    const totalPages = Number(data.totalPages);
    setTotalUsers(Number.isFinite(total) ? total : 0);
    setTotalPages(
      Number.isFinite(totalPages)
        ? totalPages
        : Math.max(1, Math.ceil((Number.isFinite(total) ? total : 0) / pageSize))
    );
    setCurrentPage(data.page ?? pageFallback);
    setHasLoadedOnce(true);
  }, [pageSize]);

  const fetchUserPage = useCallback(async (page = 1, { forceRefresh = false } = {}) => {
    if (!serverPagination) return;
    const params = buildListParams(page);
    const cached = !forceRefresh ? adminRepo.peekUserListCache(params) : null;

    if (cached) {
      applyPageData(cached, page);
    }

    if (cached && !forceRefresh) {
      probe.unlock('users 表示可能（cache）');
      return;
    }

    if (!cached) {
      probe.start('list');
      setListLoading(true);
    }

    try {
      const data = await adminRepo.listUsersPaginated(params, { forceRefresh });
      applyPageData(data);
      probe.end('list', 'ok');
      probe.unlock();
    } catch (err) {
      probe.end('list', `error: ${err?.message || 'unknown'}`);
      await showAlert({ message: err.message || 'ユーザー一覧の取得に失敗しました', variant: 'error', title: 'エラー' });
    } finally {
      setListLoading(false);
    }
  }, [
    adminRepo,
    applyPageData,
    buildListParams,
    serverPagination,
    showAlert,
    probe,
  ]);

  useEffect(() => {
    if (!serverPagination) return;

    let pageToFetch = currentPage;
    if (lastFilterSignatureRef.current !== filterSignature) {
      lastFilterSignatureRef.current = filterSignature;
      pageToFetch = 1;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    fetchUserPage(pageToFetch);
  }, [serverPagination, currentPage, filterSignature, fetchUserPage]);

  const [showModal, setShowModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false); // フェードアウト中かどうか
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    password: '',
    userId: '',
    displayName: '',
    clinicId: '',
    role: 'doctor',
    isActive: true
  });
  const [userIdTouched, setUserIdTouched] = useState(false);
  const [userIdError, setUserIdError] = useState('');
  const [isAssigningUserIds, setIsAssigningUserIds] = useState(false);
  const [loading, setLoading] = useState(false);
  // ユーザー作成完了モーダル用のステート
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultIsClosing, setResultIsClosing] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // 確認モーダル用ステート
  const [showToggleActiveModal, setShowToggleActiveModal] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [confirmActionError, setConfirmActionError] = useState('');
  const [roleList, setRoleList] = useState(DEFAULT_ROLES);
  const [expandedUserId, setExpandedUserId] = useState(null);
  
  const onUpdateRef = useRef(onUpdate);
  const pendingAssignRef = useRef(new Set());

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (lockedClinicId && showModal) {
      setFormData((prev) => (
        prev.clinicId === lockedClinicId ? prev : { ...prev, clinicId: lockedClinicId }
      ));
    }
  }, [lockedClinicId, showModal]);

  // ロール一覧を取得（ユーザー追加時の選択肢・表示名用）
  useEffect(() => {
    let cancelled = false;
    adminRepo.getRoleList().then((list) => {
      if (!cancelled) setRoleList(Array.isArray(list) && list.length ? list : DEFAULT_ROLES);
    }).catch(() => {
      if (!cancelled) setRoleList(DEFAULT_ROLES);
    });
    return () => { cancelled = true; };
  }, [adminRepo]);

  useEffect(() => {
    if (!serverPagination && clinics?.length) return;
    let cancelled = false;
    adminRepo.getAllClinics().then((list) => {
      if (!cancelled) setClinicDirectory(Array.isArray(list) ? list : []);
    }).catch(() => {
      if (!cancelled) setClinicDirectory([]);
    });
    return () => { cancelled = true; };
  }, [serverPagination, clinics?.length, adminRepo]);

  // ヘルパー関数（useEffect で使用するため先に定義）
  const getClinicName = useCallback((clinicId, user) => {
    if (!clinicId) return '—';
    if (user?.clinicName) return user.clinicName;
    const clinic = clinicOptions.find(c => c.id === clinicId);
    return clinic?.name || '—';
  }, [clinicOptions]);

  const generateRandomPassword = useCallback(() => {
    return generateSecurePassword(12, { includeSpecial: true });
  }, []);

  const normalizeUserId = useCallback((value) => {
    return (value || '').trim().toLowerCase();
  }, []);

  const buildUniqueUserId = useCallback((baseId, clinicId, takenMap) => {
    const key = clinicId || '__no_clinic__';
    if (!takenMap.has(key)) {
      takenMap.set(key, new Set());
    }
    const usedSet = takenMap.get(key);
    const safeBase = (baseId || 'user').trim() || 'user';
    let candidate = safeBase;
    let counter = 1;

    while (usedSet.has(normalizeUserId(candidate))) {
      counter += 1;
      candidate = `${safeBase}${counter}`;
    }

    usedSet.add(normalizeUserId(candidate));
    return candidate;
  }, [normalizeUserId]);

  const isDuplicateUserId = useCallback((userId, clinicId, currentUid) => {
    const normalized = normalizeUserId(userId);
    if (!normalized || !clinicId) return false;
    return displayUsers.some((user) => {
      return user.clinicId === clinicId &&
        normalizeUserId(user.userId) === normalized &&
        user.uid !== currentUid;
    });
  }, [displayUsers, normalizeUserId]);

  const getUserIdError = useCallback((userId, clinicId, currentUid) => {
    if (!normalizeUserId(userId)) {
      return 'ユーザーIDは必須です。';
    }
    if (!clinicId) {
      return 'クリニックを選択してください。';
    }
    if (isDuplicateUserId(userId, clinicId, currentUid)) {
      return '同じクリニック内で同じユーザーIDが既に使われています。';
    }
    return '';
  }, [normalizeUserId, isDuplicateUserId]);

  // 外部からのフィルタリング/モーダル表示を処理
  useEffect(() => {
    if (lockedClinicId) {
      setFilterClinicId(lockedClinicId);
      return;
    }
    // クリニックフィルターが指定された場合
    if (initialClinicFilter) {
      setFilterClinicId(initialClinicFilter);
      if (onClearFilter) {
        onClearFilter();
      }
    }
  }, [initialClinicFilter, onClearFilter, lockedClinicId]);

  useEffect(() => {
    // ユーザー追加モーダルを自動で開く
    if (autoOpenCreateClinicId || autoOpenCreateSignal) {
      const defaultRole = roleList[0]?.value || 'doctor';
      setFormData({
        password: generateRandomPassword(),
        userId: '',
        displayName: '',
        clinicId: autoOpenCreateClinicId || '',
        role: defaultRole,
        isActive: true
      });
      setUserIdTouched(false);
      setUserIdError('');
      setEditingUser(null);
      setShowModal(true);
      
      // 完了を通知
      if (onAutoOpenComplete) {
        onAutoOpenComplete();
      }
      if (onAutoOpenCreateComplete) {
        onAutoOpenCreateComplete();
      }
    }
  }, [autoOpenCreateClinicId, autoOpenCreateSignal, onAutoOpenComplete, onAutoOpenCreateComplete, generateRandomPassword, roleList]);

  useEffect(() => {
    if (!showModal) return;
    if (!userIdTouched && !formData.userId) {
      setUserIdError('');
      return;
    }
    const message = getUserIdError(formData.userId, formData.clinicId, editingUser?.uid);
    setUserIdError(message);
  }, [formData.userId, formData.clinicId, editingUser, getUserIdError, showModal, userIdTouched]);

  useEffect(() => {
    if (isAssigningUserIds) return;
    const missingUserIds = displayUsers.filter(
      (user) => !normalizeUserId(user.userId) && !pendingAssignRef.current.has(user.uid)
    );
    if (missingUserIds.length === 0) return;

    const assignUserIds = async () => {
      setIsAssigningUserIds(true);
      try {
        const takenMap = new Map();
        displayUsers.forEach((user) => {
          if (normalizeUserId(user.userId)) {
            const key = user.clinicId || '__no_clinic__';
            if (!takenMap.has(key)) {
              takenMap.set(key, new Set());
            }
            takenMap.get(key).add(normalizeUserId(user.userId));
          }
        });

        for (const user of missingUserIds) {
          pendingAssignRef.current.add(user.uid);
          const baseId = (user.userId || '').trim() || `user-${user.uid.slice(0, 6)}`;
          const uniqueUserId = buildUniqueUserId(baseId, user.clinicId, takenMap);
          await adminRepo.updateUser(user.uid, { userId: uniqueUserId });
        }
        onUpdateRef.current?.({ background: true });
      } catch (err) {
        missingUserIds.forEach((user) => pendingAssignRef.current.delete(user.uid));
        await showAlert({ message: `ユーザーIDの初期登録中にエラーが発生しました: ${err.message}`, variant: 'error', title: 'エラー' });
      } finally {
        setIsAssigningUserIds(false);
      }
    };

    assignUserIds();
  }, [displayUsers, isAssigningUserIds, normalizeUserId, buildUniqueUserId, adminRepo]);

  // フィルター処理
  const filteredUsers = useMemo(() => displayUsers.filter((user) => {
    if (!serverPagination) {
      const virtualEmail = formatVirtualEmail(user.userId, user.clinicId);
      const matchSearch =
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        virtualEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.uid?.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchSearch) return false;
      if (filterClinicId !== 'all' && user.clinicId !== filterClinicId) return false;
      if (filterRole !== 'all' && user.role !== filterRole) return false;
      if (filterStatus === 'active' && !user.isActive) return false;
      if (filterStatus === 'inactive' && user.isActive) return false;
    }

    return true;
  }), [displayUsers, serverPagination, searchTerm, filterClinicId, filterRole, filterStatus]);

  const sortedUsers = useMemo(() => {
    if (serverPagination) return displayUsers;

    const direction = sortOrder === 'asc' ? 1 : -1;
    const list = [...filteredUsers];

    list.sort((a, b) => {
      switch (sortKey) {
        case 'clinic':
          return direction * getClinicName(a.clinicId, a).localeCompare(getClinicName(b.clinicId, b), 'ja');
        case 'email': {
          const emailA = formatVirtualEmail(a.userId, a.clinicId) || a.email || '';
          const emailB = formatVirtualEmail(b.userId, b.clinicId) || b.email || '';
          return direction * emailA.localeCompare(emailB, 'ja');
        }
        case 'userId':
          return direction * (a.userId || '').localeCompare(b.userId || '', 'ja');
        case 'role':
          return direction * (a.role || '').localeCompare(b.role || '', 'ja');
        case 'status': {
          const activeA = a.isActive ? 1 : 0;
          const activeB = b.isActive ? 1 : 0;
          return direction * (activeA - activeB);
        }
        case 'displayName':
          return direction * (a.displayName || a.email || '').localeCompare(
            b.displayName || b.email || '',
            'ja'
          );
        default:
          return direction * getClinicName(a.clinicId, a).localeCompare(getClinicName(b.clinicId, b), 'ja');
      }
    });

    return list;
  }, [displayUsers, serverPagination, filteredUsers, sortKey, sortOrder, getClinicName]);

  const handleOpenCreate = () => {
    const defaultRole = roleList[0]?.value || 'doctor';
    const defaultClinicId = lockedClinicId || (filterClinicId !== 'all' ? filterClinicId : '');
    setFormData({
      password: generateRandomPassword(),
      userId: '',
      displayName: '',
      clinicId: defaultClinicId,
      role: defaultRole,
      isActive: true
    });
    setUserIdTouched(false);
    setUserIdError('');
    setEditingUser(null);
    setShowModal(true);
  };

  const handleOpenEdit = (user) => {
    setFormData({
      password: '',
      userId: user.userId || '',
      displayName: user.displayName || '',
      clinicId: user.clinicId || '',
      role: user.role || 'doctor',
      isActive: user.isActive !== undefined ? user.isActive : true
    });
    setUserIdTouched(false);
    setUserIdError('');
    setEditingUser(user);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    // フェードアウトアニメーション開始
    setIsClosing(true);
    
    // アニメーション完了後にモーダルを閉じる
    setTimeout(() => {
      setShowModal(false);
      setIsClosing(false);
      setEditingUser(null);
      setFormData({
        password: '',
        userId: '',
        displayName: '',
        clinicId: '',
        role: 'doctor',
        isActive: true
      });
      setUserIdTouched(false);
      setUserIdError('');
    }, MODAL_ANIMATION_DURATION);
  };

  const handleCloseResultModal = () => {
    setResultIsClosing(true);
    setTimeout(() => {
      setShowResultModal(false);
      setResultIsClosing(false);
      setResultData(null);
      setCopySuccess(false);
    }, MODAL_ANIMATION_DURATION);
  };

  const handleCopyLoginInfo = async () => {
    if (!resultData) return;

    // 区切りは全角「：」（半角コロン+スペースだとコピー時にスペースまで入力されやすい）
    const textToCopy =
      `クリニック名：${resultData.clinicName || '-'}\n` +
      `クリニックコード：${resultData.clinicCode || '-'}\n` +
      `ユーザーID：${resultData.userId}\n` +
      `パスワード：${resultData.password}\n` +
      `表示名：${resultData.displayName || '未設定'}\n` +
      `ロール：${getRoleLabel(resultData.role, resultData)}`;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // フォールバック: テキストエリアを使ったコピー
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // フォーム内でEnterキーを押した時の処理
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
      e.preventDefault();
      
      // 次の入力可能な要素を探す
      const form = e.target.form;
      const inputs = Array.from(form.querySelectorAll('input, select, textarea, button[type="submit"]'));
      const currentIndex = inputs.indexOf(e.target);
      const nextInput = inputs[currentIndex + 1];
      
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedUserId = formData.userId.trim();
    const userIdValidationMessage = getUserIdError(trimmedUserId, formData.clinicId, editingUser?.uid);
    if (userIdValidationMessage) {
      setUserIdError(userIdValidationMessage);
      await showAlert({ message: userIdValidationMessage, variant: 'error', title: '入力エラー' });
      return;
    }

    if (editingUser) {
      // 更新（ユーザーIDは認証と紐づくため変更不可）
      const updateData = {
        displayName: formData.displayName,
        clinicId: formData.clinicId,
        role: formData.role,
        isActive: formData.isActive
      };

      setLoading(true);
      try {
        await adminRepo.updateUser(editingUser.uid, updateData);
        await showAlert({ message: 'ユーザー情報を更新しました。', variant: 'success', title: '保存完了' });
        handleCloseModal();
        if (serverPagination) await fetchUserPage(currentPage, { forceRefresh: true });
        onUpdate();
      } catch (err) {
        await showAlert({ message: `エラーが発生しました: ${err.message}`, variant: 'error', title: 'エラー' });
      } finally {
        setLoading(false);
      }
    } else {
      // 作成
      if (!formData.password) {
        await showAlert({ message: 'パスワードは必須です。', variant: 'error', title: '入力エラー' });
        return;
      }

      const pwdCheck = validatePassword(formData.password);
      if (!pwdCheck.valid) {
        await showAlert({ message: pwdCheck.message || 'パスワードは8文字以上・英数字記号で入力してください。', variant: 'error', title: '入力エラー' });
        return;
      }

      if (!formData.clinicId) {
        await showAlert({ message: 'クリニックを選択してください。', variant: 'error', title: '入力エラー' });
        return;
      }

      // ユーザー数上限チェック
      const selectedClinic = clinicOptions.find(c => c.id === formData.clinicId);
      if (selectedClinic && selectedClinic.userLimit !== null && selectedClinic.userLimit !== undefined) {
        const clinicUserCount = getClinicUserCount(formData.clinicId);
        if (clinicUserCount >= selectedClinic.userLimit) {
          await showAlert({
            message: `このクリニックはユーザー数上限（${selectedClinic.userLimit}名）に達しています。\n新規ユーザーを追加できません。`,
            variant: 'error',
            title: '上限到達',
          });
          return;
        }
      }

      setLoading(true);
      try {
        await adminRepo.createUser({ ...formData, userId: trimmedUserId });

        // 一覧ページ断片やキャッシュ欠落でもコードが空にならないようフォールバック
        const fromOptions = clinicOptions.find((c) => c.id === formData.clinicId);
        const fromDirectory = clinicDirectory.find((c) => c.id === formData.clinicId);
        let clinicName = getClinicName(formData.clinicId);
        let clinicCode = String(fromOptions?.code || fromDirectory?.code || '').trim();
        if (!clinicCode || !clinicName || clinicName === '—') {
          try {
            const clinic = await adminRepo.getClinic(formData.clinicId);
            if (!clinicCode) clinicCode = String(clinic?.code || '').trim();
            if (!clinicName || clinicName === '—') {
              clinicName = clinic?.name || clinicName || '—';
            }
          } catch {
            // 表示フォールバック済みのため無視
          }
        }
        setResultData({
          clinicName: clinicName || '—',
          clinicCode,
          clinicId: formData.clinicId,
          userId: trimmedUserId,
          password: formData.password,
          displayName: formData.displayName,
          role: formData.role,
        });
        setShowResultModal(true);
        
        handleCloseModal();
        if (serverPagination) await fetchUserPage(currentPage, { forceRefresh: true });
        onUpdate();
      } catch (err) {
        await showAlert({ message: `エラーが発生しました: ${err.message}`, variant: 'error', title: 'エラー' });
      } finally {
        setLoading(false);
      }
    }
  };

  // 有効/無効切り替えモーダルを開く
  const handleOpenToggleActiveModal = (user) => {
    setTargetUser(user);
    setConfirmActionError('');
    setShowToggleActiveModal(true);
  };

  // 有効/無効切り替えモーダルを閉じる
  const handleCloseToggleActiveModal = () => {
    setShowToggleActiveModal(false);
    setTargetUser(null);
    setConfirmActionError('');
  };

  // 有効/無効切り替えを実行
  const handleConfirmToggleActive = async () => {
    if (!targetUser) return;
    const newStatus = !targetUser.isActive;

    try {
      await adminRepo.setUserActive(targetUser.uid, newStatus);
      handleCloseToggleActiveModal();
      if (serverPagination) await fetchUserPage(currentPage, { forceRefresh: true });
      onUpdate();
    } catch (err) {
      setConfirmActionError('エラーが発生しました: ' + err.message);
    }
  };

  // 削除モーダルを開く（一次確認）
  const handleOpenDeleteModal = async (user) => {
    if (!canDelete) return;
    const result = await requestDestructiveAction({
      title: 'ユーザー削除',
      message: `ユーザー「${user.displayName || user.userId || user.uid}」を削除します。Firebase Auth からも削除されます。`,
      confirmLabel: '削除する',
    });
    if (!result?.reason) return;

    setLoading(true);
    try {
      await adminRepo.deleteUser(user.uid, result.reason);
      if (serverPagination) await fetchUserPage(currentPage, { forceRefresh: true });
      onUpdate?.();
    } catch (err) {
      await showAlert({ message: 'エラーが発生しました: ' + err.message, variant: 'error', title: 'エラー' });
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    if (role === 'admin') return 'warning';
    return 'secondary';
  };

  const getRoleLabel = (role, user) => user?.roleLabel || getRoleLabelFromList(roleList, role);

  const previewVirtualEmail = formatVirtualEmail(formData.userId, formData.clinicId);
  const editingVirtualEmail = editingUser
    ? formatVirtualEmail(editingUser.userId || formData.userId, editingUser.clinicId || formData.clinicId)
    : previewVirtualEmail;

  const toggleUserExpanded = useCallback((uid) => {
    setExpandedUserId((prev) => (prev === uid ? null : uid));
  }, []);

  if (!embedded && serverPagination && !hasLoadedOnce && listLoading) {
    return <AdminListPageShimmer variant="user" rows={8} />;
  }

  return (
    <div className={`admin-list-container${embedded ? ' embedded-user-section' : ''}`}>
      {embedded ? (
        <div className="embedded-user-header">
          <div>
            <h2>ユーザー一覧</h2>
            <p>このクリニックに所属するユーザーの追加・編集</p>
          </div>
          <button onClick={handleOpenCreate} className="btn btn-primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            新規ユーザー
          </button>
        </div>
      ) : (
        <div className="list-header">
          <div className="section-header">
            <h1>ユーザー管理</h1>
            <p>ユーザーアカウントの管理</p>
          </div>
          <button onClick={handleOpenCreate} className="btn btn-primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            新規ユーザー
          </button>
        </div>
      )}

      <div className="list-toolbar">
        <div className="search-bar list-toolbar__search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder={embedded ? '名前・メール・IDで検索...' : 'メール、名前、ユーザーID、UIDで検索...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {!lockedClinicId && (
          <select
            className="admin-select"
            value={filterClinicId}
            onChange={(e) => setFilterClinicId(e.target.value)}
            aria-label="クリニックで絞り込み"
          >
            <option value="all">全クリニック</option>
            {clinicOptions.map(clinic => (
              <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
            ))}
          </select>
        )}

        <select
          className="admin-select"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          aria-label="ロールで絞り込み"
        >
          <option value="all">全ロール</option>
          <option value="admin">管理者</option>
          {roleList.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>

        <select
          className="admin-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          aria-label="ステータスで絞り込み"
        >
          <option value="all">全ステータス</option>
          <option value="active">有効</option>
          <option value="inactive">無効</option>
        </select>

        <div className="list-toolbar__sort">
          <span className="list-toolbar__sort-label">並び替え</span>
          <select
            className="admin-select"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            aria-label="並び替え項目"
          >
            <option value="clinic">所属</option>
            <option value="displayName">名前</option>
            <option value="email">認証用メール</option>
            <option value="userId">ユーザーID</option>
            <option value="role">ロール</option>
            <option value="status">ステータス</option>
          </select>
          <SortOrderToggle
            order={sortOrder}
            onToggle={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
          />
        </div>
      </div>

      <div className="list-result-bar">
        <div className="list-result-bar__meta">
          <span className="list-result-count">
            {serverPagination ? `${totalUsers} 件` : `${sortedUsers.length} 件`}
          </span>
          {(searchTerm || filterClinicId !== 'all' || filterRole !== 'all' || filterStatus !== 'all') && (
            <span className="list-result-hint">フィルタ適用中</span>
          )}
        </div>
        {serverPagination && (
          <ListPagination
            variant="compact"
            currentPage={currentPage}
            totalPages={totalPages}
            total={totalUsers}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            disabled={listLoading}
          />
        )}
      </div>

      {/* ユーザー一覧 */}
      <div className="admin-compact-list admin-compact-list--users">
        {serverPagination && listLoading && displayUsers.length === 0 ? (
          <AdminCompactListShimmer variant="user" rows={8} embedded={embedded} />
        ) : sortedUsers.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
            </svg>
            <p>{searchTerm || filterClinicId !== 'all' || filterRole !== 'all' || filterStatus !== 'all'
              ? '検索結果がありません。'
              : 'ユーザーが登録されていません。'}</p>
          </div>
        ) : (
          <>
            <div
              className={`admin-compact-list-header${embedded ? ' is-embedded' : ''}`}
              aria-hidden="true"
            >
              <span className="admin-compact-col-name">名前</span>
              <span className="admin-compact-col-role">ロール</span>
              {!embedded && <span className="admin-compact-col-clinic">所属</span>}
              <span className="admin-compact-col-actions" />
            </div>
            {sortedUsers.map((user) => {
              const isExpanded = expandedUserId === user.uid;
              return (
                <div
                  key={user.uid}
                  className={`admin-compact-row${user.isActive ? '' : ' is-inactive'}`}
                >
                  <div
                    className={`admin-compact-row-main${embedded ? ' is-embedded' : ''}${isExpanded ? ' is-expanded' : ''}`}
                    onClick={() => toggleUserExpanded(user.uid)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleUserExpanded(user.uid);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    aria-label={`${user.displayName || user.email} の詳細を${isExpanded ? '閉じる' : '表示'}`}
                  >
                    <div className="admin-compact-col-name">
                      <span className="admin-compact-title">{user.displayName || '名前未設定'}</span>
                      {!user.isActive && (
                        <span className="badge danger admin-compact-inline-badge">無効</span>
                      )}
                    </div>
                    <div className="admin-compact-col-role">
                      <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                        {getRoleLabel(user.role, user)}
                      </span>
                    </div>
                    {!embedded && (
                      <div className="admin-compact-col-clinic">
                        <span className="admin-compact-clinic" title={getClinicName(user.clinicId, user)}>
                          {getClinicName(user.clinicId, user)}
                        </span>
                      </div>
                    )}
                    <div
                      className="admin-compact-col-actions"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className={`btn btn-sm btn-icon admin-compact-expand-btn${isExpanded ? ' is-expanded' : ''}`}
                        onClick={() => toggleUserExpanded(user.uid)}
                        title={isExpanded ? '詳細を閉じる' : '詳細を表示'}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? '詳細を閉じる' : '詳細を表示'}
                      >
                        <IconChevronDown />
                      </button>
                    </div>
                  </div>
                  <div className={`admin-compact-detail-wrap${isExpanded ? ' is-open' : ''}`}>
                    <div className="admin-compact-detail-inner">
                      <div
                        className="admin-compact-detail-body"
                        onClick={() => handleOpenEdit(user)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleOpenEdit(user);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`${user.displayName || user.email} を編集`}
                      >
                        <dl className="admin-compact-detail-grid">
                          <div className="admin-compact-detail-item">
                            <dt>認証用メール</dt>
                            <dd className="mono">{formatVirtualEmail(user.userId, user.clinicId) || '—'}</dd>
                          </div>
                          <div className="admin-compact-detail-item">
                            <dt>ユーザーID</dt>
                            <dd className="mono">{user.userId || '未設定'}</dd>
                          </div>
                          <div className="admin-compact-detail-item">
                            <dt>状態</dt>
                            <dd>
                              {user.isActive ? (
                                <span className="badge success badge-with-icon">
                                  <IconCheck size={11} />
                                  有効
                                </span>
                              ) : (
                                <span className="badge danger badge-with-icon">
                                  <IconX size={11} />
                                  無効
                                </span>
                              )}
                            </dd>
                          </div>
                          <div className="admin-compact-detail-item admin-compact-detail-item--wide">
                            <dt>UID</dt>
                            <dd className="mono">{user.uid}</dd>
                          </div>
                        </dl>
                        <span className="admin-compact-detail-edit-hint">クリックで編集</span>
                      </div>
                      <div className="admin-compact-detail-actions admin-compact-detail-actions--end">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenToggleActiveModal(user);
                          }}
                          className={`btn btn-sm ${user.isActive ? 'btn-warning' : 'btn-primary'}`}
                        >
                          {user.isActive ? '無効化' : '有効化'}
                        </button>
                        {canDelete && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteModal(user);
                          }}
                          className="btn btn-sm btn-danger"
                          disabled={loading}
                        >
                          削除
                        </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {serverPagination && (
        <ListPagination
          variant="default"
          currentPage={currentPage}
          totalPages={totalPages}
          total={totalUsers}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          disabled={listLoading}
        />
      )}

      {/* 作成/編集モーダル */}
      {showModal && (
        <div className={`modal-overlay ${isClosing ? 'closing' : ''}`}>
          <div className={`modal ${isClosing ? 'closing' : ''}`}>
            <div className="modal-header">
              <h3>{editingUser ? 'ユーザー編集' : '新規ユーザー作成'}</h3>
              <button onClick={handleCloseModal} className="btn btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="modal-body">
              {lockedClinicId ? (
                <div className="form-group">
                  <label>クリニック</label>
                  <div style={{ padding: '10px 0', color: 'var(--color-text-primary)', fontWeight: 500 }}>
                    {getClinicName(lockedClinicId)}
                  </div>
                </div>
              ) : (
              <div className="form-group">
                <label htmlFor="clinicId">クリニック *</label>
                <select
                  id="clinicId"
                  value={formData.clinicId}
                  onChange={(e) => {
                    const nextClinicId = e.target.value;
                    setFormData({ ...formData, clinicId: nextClinicId });
                    if (userIdTouched || formData.userId) {
                      setUserIdError(getUserIdError(formData.userId, nextClinicId, editingUser?.uid));
                    }
                  }}
                  required
                >
                  <option value="">選択してください</option>
                  {clinicOptions.map(clinic => {
                    const userCount = getClinicUserCount(clinic.id);
                    const limitText = clinic.userLimit !== null && clinic.userLimit !== undefined
                      ? ` (${userCount}/${clinic.userLimit}名)`
                      : ` (${userCount}名)`;
                    const isAtLimit = clinic.userLimit !== null && userCount >= clinic.userLimit;
                    
                    return (
                      <option key={clinic.id} value={clinic.id} disabled={isAtLimit && !editingUser}>
                        {clinic.name}{limitText}{isAtLimit && !editingUser ? ' - 上限達成' : ''}
                      </option>
                    );
                  })}
                </select>
                {formData.clinicId && (() => {
                  const selectedClinic = clinicOptions.find(c => c.id === formData.clinicId);
                  if (selectedClinic && selectedClinic.userLimit !== null && selectedClinic.userLimit !== undefined) {
                    const userCount = getClinicUserCount(formData.clinicId);
                    const remaining = selectedClinic.userLimit - userCount;
                    if (remaining <= 3 && remaining > 0) {
                      return (
                        <small className="inline-icon-text" style={{ color: 'var(--color-warning)', fontSize: '0.875rem' }}>
                          <IconAlertTriangle size={14} />
                          残り{remaining}名で上限に達します
                        </small>
                      );
                    } else if (remaining === 0 && !editingUser) {
                      return (
                        <small className="inline-icon-text" style={{ color: 'var(--color-danger)', fontSize: '0.875rem' }}>
                          <IconX size={14} />
                          ユーザー数上限に達しています
                        </small>
                      );
                    }
                  }
                  return null;
                })()}
              </div>
              )}

              {!editingUser && (
                <>
                  <div className="form-group">
                    <label htmlFor="password">パスワード *（8文字以上・英数字記号）</label>
                    <div className="password-input-group">
                      <input
                        id="password"
                        type="text"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="8文字以上・英数字記号"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, password: generateRandomPassword() })}
                        className="btn btn-sm btn-secondary"
                      >
                        再生成
                      </button>
                    </div>
                  </div>

                  {previewVirtualEmail && (
                    <div className="form-group">
                      <label>認証用メール（自動生成）</label>
                      <input type="text" value={previewVirtualEmail} disabled className="mono" />
                      <span className="form-hint">
                        ユーザーIDとクリニックIDから自動生成されます（Firebase Auth 用・ログインには使用しません）
                      </span>
                    </div>
                  )}
                </>
              )}

              {editingUser && (
                <div className="form-group">
                  <label>認証用メール（自動生成）</label>
                  <input type="text" value={editingVirtualEmail || '—'} disabled className="mono" />
                  <span className="form-hint">ユーザーID変更不可のため、認証用メールも固定です</span>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="userId">ユーザーID *</label>
                <input
                  id="userId"
                  type="text"
                  value={formData.userId}
                  readOnly={!!editingUser}
                  disabled={!!editingUser}
                  onChange={editingUser ? undefined : (e) => {
                    const nextUserId = e.target.value;
                    setFormData({ ...formData, userId: nextUserId });
                    if (!userIdTouched) {
                      setUserIdTouched(true);
                    }
                    setUserIdError(getUserIdError(nextUserId, formData.clinicId, editingUser?.uid));
                  }}
                  placeholder="例: yamada"
                  required
                  aria-invalid={!!userIdError}
                  style={editingUser ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : undefined}
                />
                {!editingUser && userIdError && (
                  <small style={{ color: 'var(--color-danger)', fontSize: '0.875rem' }}>
                    {userIdError}
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="displayName">表示名</label>
                <input
                  id="displayName"
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="例: 山田 太郎"
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">ロール</label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  disabled={roleList.length === 0}
                >
                  {roleList.length === 0 ? (
                    <option value={formData.role}>読み込み中...</option>
                  ) : (
                    <>
                      {roleList.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                      <option value="admin">管理者</option>
                    </>
                  )}
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>ユーザーを有効にする</span>
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                  キャンセル
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading || roleList.length === 0}>
                  {loading ? <LoadingSpinner size="small" /> : (editingUser ? '更新' : '作成')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ユーザー作成完了モーダル */}
      {showResultModal && resultData && (
        <div className={`modal-overlay ${resultIsClosing ? 'closing' : ''}`} onClick={handleCloseResultModal}>
          <div className={`modal ${resultIsClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>ユーザー作成完了</h3>
              <button onClick={handleCloseResultModal} className="btn btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="result-info">
                <div className="result-item">
                  <span className="result-label">クリニック名</span>
                  <span className="result-value">{resultData.clinicName || '-'}</span>
                </div>
                <div className="result-item">
                  <span className="result-label">クリニックコード</span>
                  <span className="result-value mono">{resultData.clinicCode || '-'}</span>
                </div>
                <div className="result-item">
                  <span className="result-label">ユーザーID</span>
                  <span className="result-value mono">{resultData.userId}</span>
                </div>
                <div className="result-item">
                  <span className="result-label">パスワード</span>
                  <span className="result-value mono">{resultData.password}</span>
                </div>
                <div className="result-item">
                  <span className="result-label">表示名</span>
                  <span className="result-value">{resultData.displayName || '未設定'}</span>
                </div>
                <div className="result-item">
                  <span className="result-label">ロール</span>
                  <span className={`badge ${getRoleBadgeClass(resultData.role)}`}>
                    {getRoleLabel(resultData.role, resultData)}
                  </span>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  onClick={handleCopyLoginInfo} 
                  className={`btn ${copySuccess ? 'btn-success' : 'btn-secondary'}`}
                >
                  {copySuccess ? 'コピーしました' : 'ログイン情報をコピー'}
                </button>
                <button type="button" onClick={handleCloseResultModal} className="btn btn-primary">
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 有効/無効切り替え確認モーダル */}
      {showToggleActiveModal && targetUser && (
        <div className="modal-overlay" onClick={handleCloseToggleActiveModal}>
          <div className="modal" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{targetUser.isActive ? 'ユーザーを無効化' : 'ユーザーを有効化'}</h3>
              <button onClick={handleCloseToggleActiveModal} className="btn btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ 
                padding: '12px 16px',
                background: targetUser.isActive ? '#fffbeb' : '#f0fdf4',
                borderRadius: '8px',
                marginBottom: '16px',
                border: `1px solid ${targetUser.isActive ? 'rgba(245, 158, 11, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`
              }}>
                <strong>{targetUser.displayName || '名前未設定'}</strong>
                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '4px' }}>
                  {formatVirtualEmail(targetUser.userId, targetUser.clinicId) || targetUser.userId || '—'}
                </div>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                {targetUser.isActive 
                  ? 'このユーザーを無効化すると、ログインができなくなります。'
                  : 'このユーザーを有効化すると、ログインが可能になります。'}
              </p>
              {confirmActionError && (
                <div style={{ marginTop: '12px', color: '#dc2626', fontSize: '0.875rem' }}>
                  {confirmActionError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseToggleActiveModal} className="btn btn-secondary">
                キャンセル
              </button>
              <button 
                onClick={handleConfirmToggleActive} 
                className={`btn ${targetUser.isActive ? 'btn-warning' : 'btn-primary'}`}
              >
                {targetUser.isActive ? '無効化する' : '有効化する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserList;

